import type { FolderContinuationResult, ProcessResult } from '../shared/desktop-api';
import type { EngineCall } from './desktop-commands';
import { analyses, statusOf, type Analysis } from './folder-analyses';
import { runProcess, type RawProgress, type RunTicket } from './process-runs';
import { rememberRun } from './run-memory';

/**
 * A setup that outlives the tab and the engine host (services/Paige.Engine.Host/SetupRuns.cs).
 * Prod, 2026-10-07: a release restarted the engine host twice under one person's setup of a hundred
 * files; each time the run answered "send the documents again" and the screen offered only the file
 * picker. The host now starts a cut-off setup again by itself under the same run, so a screen still
 * following it sees it go on. This file is the screen's half for every other case:
 *
 * - a screen with no run of its own (another browser, a tab opened later) asks `setup-run` and
 *   follows the setup that is under way, or shows the one that finished unwatched (`findSetup`);
 * - a setup that ended through no doing of the person's is CapturePoint's `recoverable` state: the
 *   App continues it once by itself when the job opens and offers "Resume" after that, and
 *   `resumeFolderAnalysis` runs it again from the documents the workspace already holds
 *   (`resumeSetup`: a `process` with `rerunSetup` and no files). Nobody chooses a folder twice.
 */
interface SetupRun {
  state?: 'running' | 'recoverable' | 'finished' | 'none';
  runId?: string | null;
  startedAt?: string | null;
}

/** What a recoverable setup says behind "Technical details"; the screen's own words lead (setup-failure.ts). */
export const keptForResume =
  'Setup stopped before it finished. Your documents are kept: continue, and Cortexa sets this job up from them without choosing them again.';

/**
 * One operation per job for the page's lifetime: the App continues a recoverable operation by
 * itself once per operation id, so a setup that keeps stopping is offered to the person, not
 * started over and over.
 */
const operations = new Map<string, string>();

async function ask(call: EngineCall, jobId: string): Promise<SetupRun> {
  try {
    return (await call<SetupRun | null>('setup-run', { jobId })) ?? {};
  } catch {
    // A host that does not answer the question has no setup to offer: the screen starts as before.
    return {};
  }
}

function recoverable(jobId: string, startedAt?: string | null): void {
  const held = analyses.get(jobId);
  const at = startedAt ?? held?.startedAt ?? new Date().toISOString();
  if (!operations.has(jobId)) operations.set(jobId, `${jobId}:${at}`);
  analyses.set(jobId, {
    state: 'recoverable',
    startedAt: at,
    totalFileCount: held?.totalFileCount ?? 0,
    analyzedFileCount: 0,
    result: null,
    failure: keptForResume,
    evidenceRevision: 0,
    progress: null,
    operationId: operations.get(jobId),
  });
}

/**
 * The job's setup as the host holds it, for a screen with no run to follow: the id of the run under
 * way (remembered, so the caller follows it) or of one that finished unwatched, or null, with a
 * setup that can be run again put on the page as `recoverable`.
 */
export async function findSetup(call: EngineCall, jobId: string): Promise<string | null> {
  const found = await ask(call, jobId);
  if (found.state === 'running' && found.runId) {
    rememberRun(jobId, found.runId);
    return found.runId;
  }
  // Read to its end with nobody watching: its status is the proposal, shown as a reload shows it.
  if (found.state === 'finished' && found.runId) return found.runId;
  if (found.state === 'recoverable') recoverable(jobId, found.startedAt);
  return null;
}

/** A setup that just failed is offered as Resume when the host can run it again from what it holds. */
export async function recoverIfHeld(call: EngineCall, jobId: string): Promise<void> {
  if ((await ask(call, jobId)).state === 'recoverable') recoverable(jobId);
}

type Settle = (
  jobId: string,
  analysis: Analysis,
  reading: Promise<ProcessResult>,
  call?: EngineCall,
) => Promise<ProcessResult>;

/**
 * "Resume": the unfinished setup run again on the host from the documents it holds, followed to
 * its proposal like the first run (`settle` and `note` are folder-analysis.ts's own).
 */
export async function resumeSetup(
  call: EngineCall,
  jobId: string,
  settle: Settle,
  note: (jobId: string, raw: RawProgress, ticket: RunTicket) => void,
): Promise<FolderContinuationResult> {
  const analysis: Analysis = {
    state: 'running',
    startedAt: new Date().toISOString(),
    totalFileCount: analyses.get(jobId)?.totalFileCount ?? 0,
    analyzedFileCount: 0,
    result: null,
    failure: null,
    evidenceRevision: 0,
    progress: null,
    operationId: operations.get(jobId),
  };
  analyses.set(jobId, analysis);
  const stopped = new Promise<never>((_, reject) => {
    analysis.stop = () => reject(new Error('A task was canceled.'));
  });
  const id = crypto.randomUUID();
  rememberRun(jobId, id);
  const reading = runProcess<ProcessResult>(
    call,
    { jobId, id, documentTypes: [], rerunSetup: true },
    (raw, ticket) => note(jobId, raw, ticket),
  );
  const result = await settle(jobId, analysis, Promise.race([reading, stopped]), call);
  return { ...statusOf(jobId), result };
}
